/**
 * Capture — the guided shot list.
 *
 * Reads its list from `shotListFor(impact)` and its gaps from `captureGaps`, so
 * every rule about what is required and what a retake means lives in a tested
 * module rather than in this file.
 *
 * "Take" opens the phone's camera directly (`capture="environment"` on a file
 * input) rather than a custom viewfinder. The native camera is the one the
 * estimator already knows, already focuses well, and already works with gloves
 * on — a bespoke one would be worse at all three.
 *
 * But `capture` does not merely PREFER the camera, it FORBIDS everything else:
 * on a phone it opens the lens and offers no way to reach the photo library.
 * That is wrong often enough to matter — the car was photographed on the way
 * in, a customer emailed the pictures, an adjuster sent a set. So each shot
 * carries a second, capture-less input behind a "Gallery" button.
 *
 * The Gallery button only appears where `capture` is actually honoured
 * (`pointer: coarse`). On a laptop the attribute is ignored and "Take" already
 * opens the file browser, so a second button there would be two controls doing
 * one job.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  captureGaps, shotListFor,
  type ExtraShot, type ImpactPoint, type ShotState,
} from '../capture/shotlist.js';
import { downscaleImage, estimateRunCost } from '../capture/downscale.js';
import { humanBytes, photoFrom, type CapturedPhoto } from '../capture/photos.js';
import { queueMessage, type QueueSummary } from '../capture/queue.js';
import { VehicleView } from '../components/VehicleView.js';
import { useSession } from '../app/SessionProvider.js';

interface Props {
  impact: ImpactPoint;
  states: ShotState[];
  photos: Record<string, CapturedPhoto>;
  onCapture: (shotId: string, photo?: CapturedPhoto) => void;
  /** Absent in the preview build, where nothing is uploaded at all. */
  queue?: { summary: QueueSummary; online: boolean };
  /** Send the photographs to be read. The step that was missing entirely. */
  onAnalyse?: () => void;
  analysing?: boolean;
  analyseError?: string | null;
  /** How many proposals the last run produced, so the screen can say so. */
  analysed?: number | null;
  /**
   * Which model reads next. 'auto' leaves it to the server's own default -
   * everyone who never touches this picker gets exactly the behaviour they
   * always had. A concrete choice applies to the NEXT read only; nothing here
   * is remembered between jobs or sessions.
   */
  readProvider?: 'auto' | 'gemini' | 'anthropic';
  onReadProviderChange?: (p: 'auto' | 'gemini' | 'anthropic') => void;
  /** Which model actually answered the last run, so the estimator can tell
   *  the two apart on screen rather than by guessing from the results. */
  readWith?: { provider: string; model: string } | null;
  /**
   * Shots the MODEL asked for — one per part it could not see.
   *
   * Without these, "wants: underbody photograph with the bumper cover removed"
   * was a sentence in Review with nowhere to put the photograph. They are
   * appended after the impact-derived list, marked so the estimator can see
   * the app is answering its own question.
   */
  extraShots?: ExtraShot[];
  /**
   * Slot -> photograph already on the job.
   *
   * `photos` only holds shots taken in THIS session, so reopening a job showed
   * a green tick and the reference drawing — the estimator could see that
   * something had been photographed but not what. These are the ones already
   * uploaded.
   */
  photoUrls?: Record<string, string>;
  /** Throw away everything in a slot, so a wrong shot can be retaken clean. */
  onRemove?: (slotId: string) => Promise<void> | void;
}

const LABEL: Record<ShotState['status'], string> = {
  taken: '✓ Captured',
  todo: '▲ Not taken',
  retake: '↻ Retake',
};

export function Capture({ impact, states, photos, onCapture, queue,
                         onAnalyse, analysing, analyseError, analysed,
                         readProvider = 'gemini', onReadProviderChange, readWith,
                         extraShots, photoUrls, onRemove }: Props) {
  const { t } = useSession();
  const list = useMemo(() => shotListFor(impact), [impact]);
  const gaps = useMemo(() => captureGaps(list, states), [list, states]);
  const byId = useMemo(() => new Map(states.map((s) => [s.id, s])), [states]);

  const inputs = useRef<Record<string, HTMLInputElement | null>>({});
  /** The capture-less twins, behind "Gallery". Separate refs because one input
      cannot be both camera-forced and library-capable at the same time. */
  const picks = useRef<Record<string, HTMLInputElement | null>>({});
  const [busy, setBusy] = useState<string | null>(null);
  // Which slot is mid-removal, so the button cannot be pressed twice and the
  // card can say what is happening to it.
  const [clearing, setClearing] = useState<string | null>(null);
  const [failed, setFailed] = useState<Record<string, string>>({});

  const take = useCallback(async (shotId: string, file: File) => {
    setBusy(shotId);
    setFailed((f) => { const { [shotId]: _drop, ...rest } = f; return rest; });
    try {
      const { blob, plan } = await downscaleImage(file);
      onCapture(shotId, photoFrom(blob, plan, new Date().toISOString()));
    } catch (err) {
      // A photo that failed to process must NOT be marked taken — the gap is
      // the only thing that will bring the estimator back to this angle.
      setFailed((f) => ({
        ...f, [shotId]: err instanceof Error ? err.message : String(err),
      }));
    } finally {
      setBusy(null);
    }
  }, [onCapture]);

  /**
   * Throw away what is in a slot.
   *
   * Confirmed, because it is not undoable and it can take evidence off a
   * proposal with it. `onCapture(id, undefined)` clears the local card so the
   * screen does not keep showing a photograph the server no longer has.
   */
  const clear = useCallback(async (shotId: string, title: string) => {
    if (!onRemove) return;
    const ok = globalThis.confirm(
      `Remove the photograph for "${title}"?

`
      + 'This deletes it from the job. Any proposal that cited it will lose that '
      + 'evidence. It cannot be undone.');
    if (!ok) return;
    setClearing(shotId);
    try {
      await onRemove(shotId);
      onCapture(shotId, undefined);
      setFailed((f) => { const { [shotId]: _drop, ...rest } = f; return rest; });
    } catch (err) {
      setFailed((f) => ({
        ...f, [shotId]: err instanceof Error ? err.message : String(err),
      }));
    } finally {
      setClearing(null);
    }
  }, [onRemove, onCapture]);

  // Two different counts, and confusing them was a real bug.
  //
  //   `justCaptured` — photographs taken in THIS session. Their bytes are on
  //                    the device, so they are what the upload figures describe.
  //   `usable`       — every usable photograph on the job, including ones
  //                    uploaded days ago and now known only from the database.
  //
  // The button to read them must count `usable`. Counting `justCaptured` told
  // an estimator reopening a job with eight photographs to "photograph the
  // damage first".
  const justCaptured = Object.values(photos);
  const usable = states.filter((st) => st.status === 'taken').length;

  // Only photographs no model has read yet.
  //
  // Reported from the live site: photograph one panel, read it, add a second
  // photograph, read again — and the first was sent a second time, spending
  // the quota twice for an answer it already had. The bytes cannot change, so
  // a second read buys nothing. Retaking a shot clears the stamp, because
  // then the bytes really did change.
  const unread = states.filter((st) => st.status === 'taken' && !st.analysed).length;
  const alreadyRead = usable - unread;
  const cost = estimateRunCost(justCaptured);

  return (
    <section className="screen">
      <h2>{t('Capture')}</h2>
      <p className="muted lede">
        Each card shows the view with only the area you need in it. Works with no
        signal — shots queue and upload when you are back inside.
      </p>

      {gaps.complete ? (
        <div className="note good">
          <b>Shot list complete</b>
          Every area the impact implicates has been photographed.
        </div>
      ) : gaps.taken === 0 ? (
        <div className="note warn">
          <b>Nothing photographed yet</b>
          Photograph the damage — <b>one is enough to start</b>. The cards below are
          the shots that would let it judge the most, not a list you have to finish.
        </div>
      ) : (
        // Advisory, NOT a warning. The estimator has what they need to go on;
        // this says what more would buy them. Calling it "outstanding" made a
        // one-area repair look like an incomplete job, which is how an app
        // teaches people it wastes their time.
        <div className="note">
          <b>{gaps.taken} photographed · you can carry on</b>
          More shots let it judge more, and it will only propose damage it can
          actually see. Still open:{' '}
          {gaps.missing.slice(0, 4).map((m) => m.title).join(' · ')}
          {gaps.missing.length > 4 && ` · and ${gaps.missing.length - 4} more`}.
        </div>
      )}

      {queue && (() => {
        const msg = queueMessage(queue.summary, queue.online);
        if (!msg) return null;
        return (
          <div className={`note ${queue.summary.failed > 0 ? 'warn'
            : queue.online ? '' : 'good'}`}>
            <b>{queue.online ? 'Uploading' : 'No signal'}</b>
            {msg}
            {queue.summary.bytesWaiting > 0 && (
              <div className="mono small muted">
                {humanBytes(queue.summary.bytesWaiting)} still on this device
              </div>
            )}
          </div>
        );
      })()}

      <div className="shots">
        {list.map((spec) => {
          const st = byId.get(spec.id)?.status ?? 'todo';
          const reason = byId.get(spec.id)?.reason;
          const photo = photos[spec.id];
          const uploaded = photoUrls?.[spec.id];
          const error = failed[spec.id];
          const has = Boolean(photo || uploaded);
          return (
            <article key={spec.id} className={`shot ${st}`}>
              <div className="ref">
                {/* This session's photograph, else the one already uploaded,
                    else the reference drawing. A green tick with no picture
                    told the estimator something was there but not what. */}
                {photo
                  ? <img src={photo.url} alt={`Captured: ${spec.title}`} className="thumb" />
                  : uploaded
                    ? <img src={uploaded} alt={`Photographed: ${spec.title}`} className="thumb" />
                    : <VehicleView shotId={spec.id} view={spec.view} />}
              </div>
              <div className="cardtext">
                <b>{spec.title}</b>
                <p>{spec.guidance}</p>
                <div className="angle">{spec.angle}</div>
                {photo && (
                  <div className="shotmeta mono">
                    {photo.width}×{photo.height} · {humanBytes(photo.bytes)}
                  </div>
                )}
                {error && <div className="shoterr">Could not read that photo — {error}</div>}
              </div>
              <footer>
                <span>{LABEL[st]}{reason ? ` — ${reason}` : ''}</span>
                <input
                  ref={(el) => { inputs.current[spec.id] = el; }}
                  type="file" accept="image/*" capture="environment" hidden
                  aria-label={`Photograph: ${spec.title}`}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    // Clear the value so retaking the SAME file fires change again.
                    e.target.value = '';
                    if (file) void take(spec.id, file);
                  }}
                />
                {/* Same handler, no `capture` — that one attribute is the whole
                    difference between "camera only" and "camera or library". */}
                <input
                  ref={(el) => { picks.current[spec.id] = el; }}
                  type="file" accept="image/*" hidden
                  aria-label={`Choose a photograph for: ${spec.title}`}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) void take(spec.id, file);
                  }}
                />
                <div className="shotacts">
                  <button type="button" disabled={busy === spec.id || clearing === spec.id}
                          onClick={() => inputs.current[spec.id]?.click()}>
                    {busy === spec.id ? 'Reading…' : has ? 'Replace' : 'Take'}
                  </button>
                  <button type="button" className="gallery"
                          disabled={busy === spec.id || clearing === spec.id}
                          onClick={() => picks.current[spec.id]?.click()}>
                    Gallery
                  </button>
                  {has && onRemove && (
                    <button type="button" className="rm" disabled={clearing === spec.id}
                            onClick={() => void clear(spec.id, spec.title)}>
                      {clearing === spec.id ? '…' : 'Remove'}
                    </button>
                  )}
                </div>
              </footer>
            </article>
          );
        })}
      </div>

      {justCaptured.length > 0 && (
        <div className="note">
          <b>What will be sent</b>
          {justCaptured.length} photo{justCaptured.length === 1 ? '' : 's'} ·{' '}
          {humanBytes(justCaptured.reduce((sum, p) => sum + p.bytes, 0))} over your connection ·{' '}
          about {cost.imageTokens.toLocaleString()} tokens for the model to read.
          Each was resized on this device before anything left it.
        </div>
      )}

      {/*
        The step that was missing. Photographs used to be taken and then simply
        sit there: the estimator had to know to click the Review tab, and
        nothing ever asked the model to look at them. A capture screen with no
        way forward is a dead end however good the photographs are.
      */}
      {onAnalyse && (
        <div className="analyse">
          {analyseError && (
            <div className="note warn">
              <b>Could not read the photographs</b>
              {analyseError}
              {' '}Your photographs are safe — this only failed to look at them.
            </div>
          )}

          {analysed !== null && analysed !== undefined && !analysing && (
            <div className="note good">
              <b>{analysed} {analysed === 1 ? 'proposal' : 'proposals'}</b>
              {readWith?.provider && (
                <>
                  {' '}Read with <b>{readWith.provider === 'anthropic' ? 'Claude' : 'Gemini'}</b>
                  {readWith.model && <span className="muted small"> ({readWith.model})</span>}.
                </>
              )}
              {' '}Open <b>Review</b> to accept or reject each one. Nothing becomes money
              until you do.
            </div>
          )}

          {onReadProviderChange && (
            <div className="readpicker">
              <span className="lbl">Read with</span>
              <span className="optoggle" role="group" aria-label="Which AI reads these photographs">
                {(['gemini', 'anthropic'] as const).map((p) => (
                  <button key={p} type="button" disabled={analysing}
                          className={readProvider === p ? 'on' : ''}
                          aria-pressed={readProvider === p}
                          aria-label={p === 'anthropic' ? 'Claude' : 'Gemini'}
                          onClick={() => onReadProviderChange(p)}>
                    {p === 'anthropic' ? '💪' : '🪶'}
                  </button>
                ))}
              </span>
            </div>
          )}

          <button className="btn primary big"
                  disabled={unread === 0 || analysing}
                  onClick={onAnalyse}>
            {analysing
              ? 'Reading the photographs…'
              : usable === 0
                ? 'Photograph the damage first'
                : unread === 0
                  ? 'All photographs already read'
                  : `Read ${unread} new photo${unread === 1 ? '' : 's'} → Review`}
          </button>

          {/* Why the button is off, said plainly. An estimator who has just
              photographed something and finds a dead button assumes a fault. */}
          {unread === 0 && usable > 0 && !analysing && (
            <p className="muted small">
              {alreadyRead === 1 ? 'That photograph has' : `All ${alreadyRead} photographs have`}
              {' '}already been read — reading again would cost the same and tell you
              the same thing. Add a photograph, or <b>Retake</b> one, and it becomes
              readable again.
            </p>
          )}

          {unread > 0 && !analysing && (
            <p className="muted small">
              {alreadyRead > 0 && (
                <>
                  <b>{alreadyRead} already read</b> and will not be sent again.{' '}
                </>
              )}
              It proposes; you decide. Every line waits for you, and anything it
              could not see is listed beside the proposals rather than guessed at.
            </p>
          )}
        </div>
      )}

      {extraShots && extraShots.length > 0 && (
        <section className="asked">
          <h3>It asked for {extraShots.length} more
            {extraShots.length === 1 ? ' photograph' : ' photographs'}</h3>
          <p className="muted small">
            One per part it could not see. These are the shots most likely to
            change the estimate, because a part nobody photographed is a part
            nobody priced — it turns up at teardown instead.
          </p>
          <div className="shots">
            {extraShots.map((extra) => {
              const st = byId.get(extra.id)?.status ?? 'todo';
              const photo = photos[extra.id];
              const uploaded = photoUrls?.[extra.id];
              const error = failed[extra.id];
              const has = Boolean(photo || uploaded);
              return (
                <article key={extra.id} className={`shot ask ${st}`}>
                  <div className="ref">
                    {photo
                      ? <img src={photo.url} alt={`Captured: ${extra.title}`} className="thumb" />
                      : uploaded
                        ? <img src={uploaded} alt={`Photographed: ${extra.title}`} className="thumb" />
                        : <div className="askmark" aria-hidden="true">?</div>}
                  </div>
                  <div className="cardtext">
                    <b>{extra.title}</b>
                    <p>{extra.guidance}</p>
                    <div className="angle">asked for by the read</div>
                    {photo && (
                      <div className="shotmeta mono">
                        {photo.width}×{photo.height} · {humanBytes(photo.bytes)}
                      </div>
                    )}
                    {error && <div className="shoterr">Could not read that photo — {error}</div>}
                  </div>
                  <footer>
                    <span>{LABEL[st]}</span>
                    <input
                      ref={(el) => { inputs.current[extra.id] = el; }}
                      type="file" accept="image/*" capture="environment" hidden
                      aria-label={`Photograph: ${extra.title}`}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (file) void take(extra.id, file);
                      }}
                    />
                    <input
                      ref={(el) => { picks.current[extra.id] = el; }}
                      type="file" accept="image/*" hidden
                      aria-label={`Choose a photograph for: ${extra.title}`}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (file) void take(extra.id, file);
                      }}
                    />
                    <div className="shotacts">
                      <button type="button" disabled={busy === extra.id || clearing === extra.id}
                              onClick={() => inputs.current[extra.id]?.click()}>
                        {busy === extra.id ? 'Reading…' : has ? 'Replace' : 'Take'}
                      </button>
                      <button type="button" className="gallery"
                              disabled={busy === extra.id || clearing === extra.id}
                              onClick={() => picks.current[extra.id]?.click()}>
                        Gallery
                      </button>
                      {has && onRemove && (
                        <button type="button" className="rm" disabled={clearing === extra.id}
                                onClick={() => void clear(extra.id, extra.title)}>
                          {clearing === extra.id ? '…' : 'Remove'}
                        </button>
                      )}
                    </div>
                  </footer>
                </article>
              );
            })}
          </div>
        </section>
      )}

      <p className="muted small">
        The downscaled photo is the original of record: it is stored once, never
        overwritten, and markup saves as a new asset beside it. Downscaling on the
        device is what keeps the reading inside its cost budget — and what makes it
        work on a workshop connection. Shots are held on this device until they are
        safely uploaded, so losing signal costs you nothing.
      </p>
    </section>
  );
}
